import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { galleryApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { fadeInUp, staggerContainer } from '../../utils/animations'
import { resolveMediaUrl } from '../../utils/media'

const FALLBACK = ['/slider/001.JPG', '/slider/002.JPG', '/slider/003.JPG', '/slider/004.JPG']

export default function GalleryPage() {
  const { t } = useTranslation()
  const { localized } = useLanguage()
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => {
    galleryApi
      .getAll()
      .then(({ data }) => setImages(data.data || data || []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false))
  }, [])

  const displayItems =
    images.length > 0
      ? images
      : FALLBACK.map((url, i) => ({ id: i, image: url, image_url: url }))

  return (
    <div className="header-offset">
      <div className="gradient-brand py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold"
          >
            {t('gallery.title')}
          </motion.h1>
          <p className="mt-4 text-white/80">{t('gallery.subtitle')}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {loading ? (
          <LoadingSpinner className="py-20" />
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="columns-1 gap-4 sm:columns-2 lg:columns-3"
          >
            {displayItems.map((item, index) => {
              const src = resolveMediaUrl(item.image || item.image_url || item.url, FALLBACK[index % FALLBACK.length])
              return (
                <motion.button
                  key={item.id || index}
                  type="button"
                  variants={fadeInUp}
                  onClick={() => setLightbox({ src, caption: localized(item, 'title') || localized(item, 'caption') })}
                  className="group mb-4 block w-full overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <img
                    src={src}
                    alt={localized(item, 'title') || localized(item, 'caption') || `Gallery ${index + 1}`}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </motion.button>
              )
            })}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute end-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={lightbox.src}
              alt={lightbox.caption}
              className="max-h-[90vh] max-w-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
